import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AddPropertyValues,
  Invite,
  IProperty,
  IUser,
  UpdatePropertyRequest,
  UserRole,
} from '@/types';

interface PropertyState {
  property: IProperty | null;
  loading: boolean;
  error: string | null;
  serverErrors: { [key: string]: string[] } | null;
  isPropertyCreated: boolean;
  isPropertyUpdated: boolean;
  isPropertyDeleted: boolean;
}

const initialState: PropertyState = {
  property: null,
  loading: false,
  error: null,
  serverErrors: null,
  isPropertyCreated: false,
  isPropertyUpdated: false,
  isPropertyDeleted: false,
};

const propertySlice = createSlice({
  name: 'property',
  initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchingProperty(state, _action: PayloadAction<number>) {
      state.loading = true;
      state.error = null;
    },
    fetchingPropertySuccess(state, action: PayloadAction<IProperty>) {
      state.property = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchingPropertyError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createProperty(state, _action: PayloadAction<AddPropertyValues>) {
      state.loading = true;
      state.error = null;
    },
    createPropertySuccess(state, action) {
      state.property = action.payload;
      state.loading = false;
      state.error = null;
      state.isPropertyCreated = true;
    },
    createPropertyError(state, action) {
      state.error = action.payload;
      state.loading = false;
      state.isPropertyCreated = false;
    },
    updatingProperty(state, _action: PayloadAction<UpdatePropertyRequest>) {
      state.loading = true;
      state.error = null;
    },
    updatingPropertySuccess(state, action: PayloadAction<IProperty>) {
      state.property = action.payload;
      state.loading = false;
      state.error = null;
      state.isPropertyUpdated = true;
    },
    updatingPropertyError(state, action) {
      state.error = action.payload;
      state.loading = false;
      state.isPropertyUpdated = false;
    },
    deletingProperty(state, _action: PayloadAction<number>) {
      state.loading = true;
      state.error = null;
    },
    deletingPropertySuccess(state) {
      state.property = null;
      state.loading = false;
      state.error = null;
      state.isPropertyDeleted = true;
    },
    deletingPropertyError(state, action) {
      state.error = action.payload;
      state.loading = false;
      state.isPropertyDeleted = false;
    },
    setIsPropertyCreated(state, action: PayloadAction<boolean>) {
      state.isPropertyCreated = action.payload;
    },
    setIsPropertyUpdated(state, action: PayloadAction<boolean>) {
      state.isPropertyUpdated = action.payload;
    },
    setIsPropertyDeleted(state, action: PayloadAction<boolean>) {
      state.isPropertyDeleted = action.payload;
    },
    setProperty(state, action: PayloadAction<IProperty | null>) {
      state.property = action.payload;
    },
  },
});

// Существующие селекторы
export const propertyLoadingSelector = (state: PropSavyRootState) =>
  state.property.loading;

export const propertyErrorSelector = (state: PropSavyRootState) =>
  state.property.error;

export const propertySelector = (state: PropSavyRootState) =>
  state.property.property;

export const isPropertyCreatedSelector = (state: PropSavyRootState) =>
  state.property.isPropertyCreated;

export const isPropertyUpdatedSelector = (state: PropSavyRootState) =>
  state.property.isPropertyUpdated;

export const isPropertyDeletedSelector = (state: PropSavyRootState) =>
  state.property.isPropertyDeleted;

// 🆕 НОВЫЕ селекторы для работы с ролями и владельцами
export const propertyOwnerSelector = (state: PropSavyRootState) =>
  state.property.property?.owner || null;

export const propertyOwnerRoleSelector = (state: PropSavyRootState) =>
  state.property.property?.owner?.role || null;

// Проверка, является ли текущий пользователь владельцем этой недвижимости
export const isCurrentUserPropertyOwnerSelector =
  (currentUserId: number) => (state: PropSavyRootState) =>
    state.property.property?.owner?.id === currentUserId;

// Получение информации о владельце недвижимости
export const propertyOwnerInfoSelector = (state: PropSavyRootState) => {
  const owner = state.property.property?.owner;
  if (!owner) return null;

  return {
    id: owner.id,
    name: `${owner.firstname} ${owner.lastname}`,
    email: owner.email,
    role: owner.role,
    avatar: owner.avatar,
  };
};

// Селектор для проверки прав доступа к недвижимости
export const canManagePropertySelector =
  (currentUserId: number, currentUserRole: UserRole | null) =>
  (state: PropSavyRootState) => {
    const property = state.property.property;
    if (!property) return false;

    // Owner может управлять всеми свойствами, которыми владеет
    if (
      currentUserRole === UserRole.OWNER &&
      property.owner?.id === currentUserId
    ) {
      return true;
    }

    // Manager может управлять недвижимостью, если владелец дал такие права
    // (логика может быть более сложной в зависимости от бизнес-правил)
    if (currentUserRole === UserRole.MANAGER) {
      return true; // Пока что все менеджеры могут управлять
    }

    return false;
  };

export const {
  fetchingProperty,
  fetchingPropertySuccess,
  fetchingPropertyError,
  createProperty,
  createPropertySuccess,
  createPropertyError,
  updatingProperty,
  updatingPropertySuccess,
  updatingPropertyError,
  setIsPropertyCreated,
  setIsPropertyUpdated,
  setIsPropertyDeleted,
  setProperty,
  deletingProperty,
  deletingPropertySuccess,
  deletingPropertyError,
} = propertySlice.actions;

export default propertySlice.reducer;
